import { useState } from 'react'
import PropTypes from 'prop-types'
import { IconFill, IconOffset } from '../../../ToolSelectorTop/Icons.jsx'
import InputSpinner2 from '../InputSpinner2/InputSpinner2.jsx'

const ACTIONS = [
    { key: 'offset', label: 'Offset', description: 'Contorno alrededor', Icon: IconOffset },
    { key: 'fill', label: 'Fill', description: 'Silueta sin huecos', Icon: IconFill },
]

const JOIN_TYPES = [
    { value: 'round', label: 'Redondeado' },
    { value: 'miter', label: 'Punta' },
    { value: 'square', label: 'Recto' },
]

/**
 * Offset y fill como dibujo (C3D-16): la misma operación que crea el fondo,
 * pero el resultado entra como un dibujo nuevo justo debajo de la selección,
 * y el fondo no se toca. Se abre desde la barra izquierda porque es de uso
 * frecuente (bordes alrededor de textos y dibujos).
 *
 * La distancia es la misma que la del panel del fondo; el tipo de esquina y el
 * detalle son de este panel.
 */
export default function OffsetDrawingPanel({
    offsetLength,
    onOffsetLengthChange,
    onApply,
    loadingAction = null,
    error = null,
}) {
    const [joinType, setJoinType] = useState('round')
    const [highPrecision, setHighPrecision] = useState(false)
    const busy = Boolean(loadingAction)

    return (
        <div className="flex flex-col gap-3 pb-5">
            <div className="grid grid-cols-2 gap-2">
                {ACTIONS.map(({ key, label, description, Icon }) => {
                    const loading = loadingAction === key
                    return (
                        <button
                            key={key}
                            type="button"
                            disabled={busy}
                            onClick={() => onApply(key, { offsetJoinType: joinType, highPrecision, asDrawing: true })}
                            className={`group flex min-h-[104px] flex-col items-center justify-center rounded-lg border border-base-300 bg-base-100 p-3 text-base-content shadow-sm transition-all ${
                                busy ? 'cursor-wait opacity-70' : 'cursor-pointer hover:border-primary/60 hover:bg-base-200 hover:shadow-md'
                            }`}
                        >
                            {loading
                                ? <span className="loading loading-spinner loading-md mb-2"></span>
                                : <Icon className="mb-2 h-10 w-10 text-base-content/90 transition-colors group-hover:text-primary" />}
                            <span className="text-sm font-semibold leading-tight">{loading ? 'Calculando...' : label}</span>
                            <span className="mt-1 text-xs leading-tight text-base-content/60">{description}</span>
                        </button>
                    )
                })}
            </div>

            {error && (
                <div className="rounded-md border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">{error}</div>
            )}

            <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="offset_drawing_length">Distancia (mm)</label>
                <InputSpinner2
                    value={offsetLength}
                    onChange={onOffsetLengthChange}
                    min={0}
                    max={1000}
                    initialStep={1}
                    interval={100}
                />
            </div>

            <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="offset_drawing_join">Esquinas</label>
                <select
                    id="offset_drawing_join"
                    className="select select-bordered select-sm w-full bg-base-100 text-base-content"
                    value={joinType}
                    disabled={busy}
                    onChange={(event) => setJoinType(event.target.value)}
                >
                    {JOIN_TYPES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
            </div>

            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-base-300 p-3">
                <div>
                    <span className="block text-sm font-medium">Máximo detalle</span>
                    <span className="block text-xs text-base-content/60">Más precisión en las curvas, sin simplificar.</span>
                </div>
                <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={highPrecision}
                    disabled={busy}
                    onChange={(event) => setHighPrecision(event.target.checked)}
                />
            </label>

            <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs leading-snug">
                Selecciona lo que quieras rodear (un texto, por ejemplo) y pulsa <strong>Offset</strong> o
                <strong> Fill</strong>. La forma entra como un <strong>dibujo nuevo</strong> justo debajo de la
                selección; sin selección se calcula sobre todo el diseño. El fondo no cambia. Puedes
                seleccionar otra cosa sin cerrar este panel.
            </div>
        </div>
    )
}

OffsetDrawingPanel.propTypes = {
    offsetLength: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onOffsetLengthChange: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    loadingAction: PropTypes.string,
    error: PropTypes.string,
}
